import {
  useMemo,
  useCallback,
  useState,
  useEffect,
  forwardRef,
} from 'react';
import { nanoid } from 'nanoid';
import { useCallbackRef, useMergeRefs } from 'use-callback-ref';
import { css } from '@emotion/core';
import styled from '@emotion/styled';

import Input from '../Input';
import Error from '../Error';
import ScreenReaderAlert from '../ScreenReaderAlert';

const Container = styled.div`
  position: relative;
  display: flex;

  input {
    flex-grow: 2;
  }
`;

const warningStyling = ({ maxLength, count }) => {
  const remaining = maxLength - count;

  if (count === maxLength) {
    return css`
      color: red;
    `;
  }

  if (remaining <= 10) {
    return css`
      color: #ff5300b3;
    `;
  }

  return css``;
};

const Remaining = styled.span`
  position: absolute;
  top: -20px;
  right: 0px;
  font-size: 12px;
  color: #00000080;

  ${warningStyling}
`;

const produceValue = (value, maxLength) => {
  if (value && value.length > maxLength) {
    return value.slice(0, maxLength);
  }

  return value;
};

const produceDefaultRemaining = (value, defaultValue) => {
  if (value) {
    return value.length;
  }

  if (defaultValue) {
    return defaultValue.length;
  }

  return 0;
};

/* This input can either be used as a controlled or uncontrolled component. It will check to see
 * if a defaultValue (uncontrolled) or value (controlled) is provided and will trim the associated values
 * at which point it will update the parent component
 *
 * Since we already supply the input with an aria-describedby to link it to the 'Character limit reached' error,
 * we need to intercept any incoming aria-describedby prop so that so that we can also link it to the input
 */
function InputWithLengthWarnings(props, ref) {
  const {
    id,
    maxLength = 80,
    defaultValue,
    value,
    onChange,
    className,
    'aria-describedby': ariaDescribedBy,
    isError,
    ...rest
  } = props;

  const trimmedDefaultValue = useMemo(() => produceValue(defaultValue, maxLength), [defaultValue, maxLength]);

  const trimmedValue = useMemo(() => produceValue(value, maxLength), [value, maxLength]);

  const [charactersUsed, setCharactersUsed] = useState(produceDefaultRemaining(value, defaultValue));

  // Will force update the parent component if value changes
  useEffect(() => {
    if (onChange && trimmedValue !== value) {
      onChange(trimmedValue);
    }
  }, [onChange, trimmedValue, value]);

  const handleLengthChange = useCallback((e) => {
    setCharactersUsed(e.target.value.length);
    if (onChange) {
      onChange(e.target.value);
    }
  }, [onChange]);

  // Since react-hook-form utilizes refs in order to set the value of the input
  // We need to be able to detect when react-hook-form 'registers' the input at which
  // point we can set the remaining value of the input. Without this ref-callback, it is
  // not possible to detect when react-hook-form modifies the dom and we therefore cannot
  // initialize the remaining counter
  const inputRef = useCallbackRef(null, (node) => {
    if (node) {
      const formValue = node.value;
      const trimmedFormValue = produceValue(formValue, maxLength);

      node.value = trimmedFormValue;
      setCharactersUsed(trimmedFormValue.length);
    }
  });

  const mergedRefs = useMergeRefs([ref, inputRef]);

  // If id is not passed in via props, then we need to create one so that the error
  // associated with the Input can be tied to it
  const warningId = useMemo(() => `error-max-limit-reached-${id || nanoid()}`, [id]);

  return (
    <Container className={className}>
      <Input
        id={id}
        title={`Max characters ${maxLength}`}
        type="text"
        defaultValue={trimmedDefaultValue}
        value={trimmedValue}
        maxLength={maxLength}
        ref={mergedRefs}
        onChange={handleLengthChange}
        aria-describedby={`${warningId} ${ariaDescribedBy}`.trim()}
        isError={isError}
        {...rest}
      />
      <Remaining
        aria-hidden
        aria-live="polite"
        data-testid="remaining-characters"
        title="Remaining characters"
        count={charactersUsed}
        maxLength={maxLength}
      >
        {charactersUsed}
        /
        {maxLength}
      </Remaining>
      <ScreenReaderAlert>
        <Error
          role="alert"
          id={warningId}
          isError={maxLength === charactersUsed}
        >
          Character limit reached
        </Error>
      </ScreenReaderAlert>
    </Container>
  );
}

export default forwardRef(InputWithLengthWarnings);
